/**
 * Module Registry
 *
 * Each module exports { routes, nav }.
 * To add a new module (e.g. CRM):
 *   1. Create modules/crm.js with routes and nav
 *   2. Import and add to the `modules` array below with its slug
 *
 * External modules (in /modules/<Name>/Resources/js/module.js) are
 * auto-discovered via loader.js and merged automatically — no manual
 * import needed. Legacy modules below take precedence if slugs overlap.
 *
 * Slugs match the `modules` table in the database.
 * Modules without a slug (dashboard, settings, portal) are always visible.
 */
import { useOrgStore } from '@/stores/org';
import { getExternalModules } from './loader';

import dashboard from './dashboard';
import drive from './drive';
// Contacts has been extracted to modules/Contacts/ (auto-discovered via loader.js)
// Opportunities has been extracted to modules/Opportunities/ (auto-discovered via loader.js)
// Emails has been extracted to modules/Emails/ (auto-discovered via loader.js)
// Projects has been extracted to modules/Projects/ (auto-discovered via loader.js)
// Tasks has been extracted to modules/Tasks/ (auto-discovered via loader.js)
// Calendar has been extracted to modules/Calendar/ (auto-discovered via loader.js)
// Timesheets has been extracted to modules/Timesheets/ (auto-discovered via loader.js)
// Mileages has been extracted to modules/Mileages/ (auto-discovered via loader.js)
// Contracts has been extracted to modules/Contracts/ (auto-discovered via loader.js)
// Accounting has been extracted to modules/Accounting/ (auto-discovered via loader.js)
// Products has been extracted to modules/Products/ (auto-discovered via loader.js)
// Inventory has been extracted to modules/Inventory/ (auto-discovered via loader.js)
// Hosting has been extracted to modules/Hosting/ (auto-discovered via loader.js)
// Payroll has been extracted to modules/Payroll/ (auto-discovered via loader.js)
// Support has been extracted to modules/Support/ (auto-discovered via loader.js)
// Reports has been extracted to modules/Reports/ (auto-discovered via loader.js)
import settings from './settings';
import usage from './usage';
import billing from './billing';
// Equipment has been extracted to modules/Equipment/ (auto-discovered via loader.js)
// Marketing has been extracted to modules/Marketing/ (auto-discovered via loader.js)
// Surveys has been extracted to modules/Surveys/ (auto-discovered via loader.js)
// Forms has been extracted to modules/Forms/ (auto-discovered via loader.js)
import portal from './portal';

const legacyModules = [
    { ...dashboard, ability: 'dashboard' },
    { ...drive },
    // Contacts is now auto-discovered from modules/Contacts/
    // Opportunities is now auto-discovered from modules/Opportunities/
    // Emails is now auto-discovered from modules/Emails/
    // Projects is now auto-discovered from modules/Projects/
    // Tasks is now auto-discovered from modules/Tasks/
    // Calendar is now auto-discovered from modules/Calendar/
    // Contracts is now auto-discovered from modules/Contracts/
    // Timesheets is now auto-discovered from modules/Timesheets/
    // Mileages is now auto-discovered from modules/Mileages/
    // Accounting is now auto-discovered from modules/Accounting/
    // Products is now auto-discovered from modules/Products/
    // Inventory is now auto-discovered from modules/Inventory/
    // Hosting is now auto-discovered from modules/Hosting/
    // Payroll is now auto-discovered from modules/Payroll/
    // Support is now auto-discovered from modules/Support/
    // Equipment is now auto-discovered from modules/Equipment/
    // Marketing is now auto-discovered from modules/Marketing/
    // Surveys is now auto-discovered from modules/Surveys/
    // Forms is now auto-discovered from modules/Forms/
    // Reports is now auto-discovered from modules/Reports/
    { ...settings },
    { ...usage },
    { ...billing },
    { ...portal }
];

// Merge external modules, skipping any whose slug already exists in legacy
const legacySlugs = new Set(legacyModules.map(m => m.slug).filter(Boolean));
const externalModules = getExternalModules().filter(m => {
    const slug = m.slug || m._dirSlug;
    return slug && !legacySlugs.has(slug);
});

const modules = [...legacyModules, ...externalModules];

/**
 * Get all organization-level routes (children of /organization/:org)
 * Portal routes are excluded — they are top-level.
 *
 * Every route carries its module in `meta.module`; the navigation guard in
 * routes.js refuses the ones the organization's plan does not include. The
 * route table itself stays whole because it is built once, at boot, before any
 * organization is known and for every organization the session then visits —
 * filtering it here would freeze the first org's plan into the second's.
 *
 * Settings routes contributed by modules via `settingsRoutes` are merged
 * into the settings parent route's children automatically.
 */
export function getOrgRoutes() {
    // Collect all settingsRoutes from modules that contribute them
    const allSettingsRoutes = modules.filter(m => Array.isArray(m.settingsRoutes)).flatMap(m => m.settingsRoutes);

    // Collect all operationsRoutes from modules that contribute tabs to settings-operations
    const allOperationsRoutes = modules.filter(m => Array.isArray(m.operationsRoutes)).flatMap(m => m.operationsRoutes);

    // Collect all generalRoutes from modules that contribute tabs to settings-general
    const allGeneralRoutes = modules.filter(m => Array.isArray(m.generalRoutes)).flatMap(m => m.generalRoutes);

    // Collect all crmRoutes from modules that contribute tabs to settings-crm
    const allCrmRoutes = modules.filter(m => Array.isArray(m.crmRoutes)).flatMap(m => m.crmRoutes);

    // Collect all integrationRoutes from modules that contribute tabs to settings-integrations
    const allIntegrationRoutes = modules
        .filter(m => Array.isArray(m.integrationRoutes))
        .flatMap(m => m.integrationRoutes);

    // Collect all logRoutes from modules that contribute tabs to settings-logs
    const allLogRoutes = modules.filter(m => Array.isArray(m.logRoutes)).flatMap(m => m.logRoutes);

    // Collect all billingRoutes from modules that contribute tabs to settings-billing
    const allBillingRoutes = modules.filter(m => Array.isArray(m.billingRoutes)).flatMap(m => m.billingRoutes);

    return modules
        .filter(m => m.nav !== null) // exclude portal (top-level, nav is null)
        .flatMap(m => {
            return (m.routes || []).map(route => {
                // Inject settingsRoutes into the settings parent route
                if (route.name === 'settings') {
                    const settingsChildren = [...(route.children || []), ...allSettingsRoutes];
                    // Also inject operationsRoutes / crmRoutes / integrationRoutes into their vertical-tab parent routes
                    const children = settingsChildren.map(child => {
                        if (child.name === 'settings-operations') {
                            return { ...child, children: [...(child.children || []), ...allOperationsRoutes] };
                        }
                        if (child.name === 'settings-general') {
                            return { ...child, children: [...(child.children || []), ...allGeneralRoutes] };
                        }
                        if (child.name === 'settings-crm') {
                            return { ...child, children: [...(child.children || []), ...allCrmRoutes] };
                        }
                        if (child.name === 'settings-integrations') {
                            return { ...child, children: [...(child.children || []), ...allIntegrationRoutes] };
                        }
                        if (child.name === 'settings-logs') {
                            return { ...child, children: [...(child.children || []), ...allLogRoutes] };
                        }
                        if (child.name === 'settings-billing') {
                            return { ...child, children: [...(child.children || []), ...allBillingRoutes] };
                        }
                        return child;
                    });
                    return { ...route, children };
                }
                // Inject meta.ability and meta.module into each top-level route —
                // children inherit both via Vue Router meta merge
                const slug = m.slug || m._dirSlug;
                if (!m.ability && !slug) return route;
                return {
                    ...route,
                    meta: {
                        ...route.meta,
                        ...(m.ability ? { ability: m.ability } : {}),
                        ...(slug ? { module: slug } : {})
                    }
                };
            });
        });
}

/**
 * Get top-level routes (portal, etc.)
 * Portal routes contributed by modules via `portalRoutes` are merged
 * into the authenticated portal route's children automatically.
 *
 * Each one carries `meta.module`, the slug of the module contributing it: the
 * server closes every portal route of a non-core module with the `module:`
 * middleware, so a screen the organization's plan does not include must not
 * open — a bookmark or a link in an email reaches it without passing the menu.
 */
export function getTopLevelRoutes() {
    const portalRoutes = modules
        .filter(m => Array.isArray(m.portalRoutes))
        .flatMap(m => {
            const slug = m.slug || m._dirSlug;
            return m.portalRoutes.map(route => (slug ? { ...route, meta: { ...route.meta, module: slug } } : route));
        });

    return modules
        .filter(m => m.nav === null) // portal has nav: null
        .flatMap(m => m.routes)
        .map(route => {
            // Inject discovered portalRoutes into the authenticated portal route's children
            if (route.children && portalRoutes.length > 0) {
                return { ...route, children: [...route.children, ...portalRoutes] };
            }
            return route;
        });
}

/**
 * Get report sections from all modules, filtered by enabled modules and abilities.
 * Each module with a `reports` function contributes a section { title, items }.
 * @returns {Array} Report sections for the reports index page
 */
export function getReportSections() {
    const orgStore = useOrgStore();

    return modules
        .filter(m => typeof m.reports === 'function')
        .filter(m => !m.slug || orgStore.isModuleEnabled(m.slug))
        .filter(m => !m.ability || orgStore.canAny(m.ability))
        .flatMap(m => {
            const result = m.reports();
            return Array.isArray(result) ? result : [result];
        });
}

/**
 * Get all dashboard widgets from modules, filtered by enabled modules and abilities.
 * Each module with a `widgets` function contributes an array of widget definitions.
 * @returns {Array} Widget definitions with module/ability injected
 */
export function getWidgetRegistry() {
    const orgStore = useOrgStore();

    return modules
        .filter(m => typeof m.widgets === 'function')
        .filter(m => !m.slug || orgStore.isModuleEnabled(m.slug))
        .filter(m => !m.ability || orgStore.canAny(m.ability))
        .flatMap(m =>
            m.widgets().map(w => ({
                ...w,
                module: m.slug || null,
                moduleName: m.name || null,
                ability: w.ability || m.ability || null
            }))
        )
        .filter(w => !w.ability || holdsAbility(w.ability));
}

/**
 * Whether the user holds an ability the way the server's `ability:` middleware
 * reads it (`RoleService::canAny`): the ability itself, anything under it
 * (`.own`), or an ancestor that covers it (a bare `accounting`).
 *
 * Answers « no » until the roles are known: a widget painted then taken back
 * has already shown the figures it was meant to hide.
 */
export function holdsAbility(ability) {
    const orgStore = useOrgStore();
    if (!gatesAnswerable()) return false;
    return orgStore.canAny(ability) || orgStore.can(ability);
}

/**
 * Whether the gates below can be trusted to answer.
 *
 * `can()` / `canAny()` / `hasRole()` are permissive until `loadRoles()` resolves
 * — deliberately, so a form's buttons do not vanish and reappear while the page
 * settles. A menu is the opposite case: being permissive there paints every
 * section of the application, then takes back the ones this person never had.
 * A boundary that shows what it is about to hide is not a boundary, which is
 * the same reason `isModuleEnabled` already answers `false` while it loads.
 *
 * So every rail and menu below answers « nothing yet » rather than « everything
 * for now ». `loadRoles()` runs from AppLayout on mount, so the wait is one
 * round-trip, and the lists rebuild by themselves when it lands.
 */
function gatesAnswerable() {
    return useOrgStore().rolesLoaded;
}

/**
 * Get all settings sections from modules, filtered by enabled modules and abilities.
 * Each module with a `settings` function contributes sections with menu items.
 * Items are filtered at render-time in Settings.vue based on permissions/admin/module checks.
 * @returns {Array} Settings sections sorted by order, each with { title, order, items }
 */
export function getSettingsSections() {
    const orgStore = useOrgStore();

    if (!gatesAnswerable()) return [];

    const raw = modules
        .filter(m => typeof m.settings === 'function')
        .filter(m => !m.slug || orgStore.isModuleEnabled(m.slug))
        .flatMap(m => {
            const result = m.settings();
            return Array.isArray(result) ? result : [result];
        });

    // Merge sections with the same title
    const merged = new Map();
    for (const section of raw) {
        const existing = merged.get(section.title);
        if (existing) {
            existing.items.push(...section.items);
        } else {
            merged.set(section.title, { ...section, items: [...section.items] });
        }
    }

    return [...merged.values()]
        .sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
        .map(section => ({
            ...section,
            items: [...section.items].sort((a, b) => (a.position ?? 99) - (b.position ?? 99))
        }));
}

/**
 * The settings sections this person can actually open, empty sections dropped.
 *
 * The same question has to be answered in two places — the menu that lists them
 * and the sidebar entry that leads to the menu — and answering it twice is how
 * « Settings » ends up pointing at an empty screen. So it is answered once,
 * here.
 *
 * @returns {Array} Sections with a `visibleItems` array, each holding at least one
 */
export function getVisibleSettingsSections() {
    const orgStore = useOrgStore();

    const isItemVisible = item => {
        // A shell entry decides for itself — e.g. Logs is worth showing only when
        // at least one of its tabs is readable by this user.
        if (typeof item.visible === 'function' && !item.visible()) return false;
        if (item.admin && !orgStore.isAdmin) return false;
        if (item.module && !orgStore.isModuleEnabled(item.module)) return false;
        if (item.ability && !orgStore.can(item.ability)) return false;
        // Legacy: permission field maps to settings.X ability
        if (item.permission && !orgStore.can('settings.' + item.permission)) return false;
        return true;
    };

    return getSettingsSections()
        .map(section => ({ ...section, visibleItems: section.items.filter(isItemVisible) }))
        .filter(section => section.visibleItems.length > 0);
}

/**
 * Get the integration tabs rendered in the settings-integrations vertical tab menu.
 * Each integration module contributes an `integrationTabs` function returning tab
 * definitions. Filtered by enabled module and abilities, sorted alphabetically by title.
 * @returns {Array} Tab definitions { title, icon, route }
 */
export function getIntegrationTabs() {
    const orgStore = useOrgStore();

    if (!gatesAnswerable()) return [];

    return modules
        .filter(m => typeof m.integrationTabs === 'function')
        .filter(m => !m.slug || orgStore.isModuleEnabled(m.slug))
        .filter(m => !m.ability || orgStore.canAny(m.ability))
        .flatMap(m => m.integrationTabs())
        .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
}

/**
 * Get the log tabs rendered in the settings-logs vertical tab menu.
 * Each module contributing a read-only trail declares a `logTabs` function.
 * Filtered by enabled module and abilities, then by each tab's own `admin` /
 * `ability` gate, sorted alphabetically by title — the rail grows as modules are
 * added, and an alphabetical rail is the one a reader can scan.
 * @returns {Array} Tab definitions { title, icon, route }
 */
export function getLogTabs() {
    const orgStore = useOrgStore();

    if (!gatesAnswerable()) return [];

    return modules
        .filter(m => typeof m.logTabs === 'function')
        .filter(m => !m.slug || orgStore.isModuleEnabled(m.slug))
        .filter(m => !m.ability || orgStore.canAny(m.ability))
        .flatMap(m => m.logTabs())
        .filter(tab => !tab.admin || orgStore.isAdmin)
        .filter(tab => !tab.ability || orgStore.can(tab.ability))
        .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
}

/**
 * Get the tabs rendered in the settings-general vertical tab menu.
 * Cross-cutting configuration: what an organization sets whatever it bought.
 * Each contributing module declares a `generalTabs` function; core tabs come
 * from `settings.js`. Filtered by enabled module and by each tab's own
 * `ability` gate, then ordered by `position` — this rail reads in a fixed
 * order (the application, then the calendar, the tasks, the files), not
 * alphabetically, because that order is the one the settings were grouped in.
 * @returns {Array} Tab definitions { title, icon, route }
 */
export function getGeneralTabs() {
    const orgStore = useOrgStore();

    if (!gatesAnswerable()) return [];

    return modules
        .filter(m => typeof m.generalTabs === 'function')
        .filter(m => !m.slug || orgStore.isModuleEnabled(m.slug))
        .flatMap(m => m.generalTabs())
        .filter(tab => !tab.module || orgStore.isModuleEnabled(tab.module))
        .filter(tab => !tab.admin || orgStore.isAdmin)
        .filter(tab => !tab.ability || orgStore.can(tab.ability))
        .sort((a, b) => (a.position ?? 99) - (b.position ?? 99));
}

/**
 * Get the tabs rendered in the settings-billing vertical tab menu.
 *
 * What an organization consumes and what it owes for it — two readings of one
 * subject, which is why they share a screen rather than two entries in the
 * settings list. A module that sells its own consumption tomorrow adds its tab
 * here, with its own permission, and the screen does not change.
 *
 * Filtered and sorted like the General tabs.
 *
 * @returns {Array} Tab definitions { title, icon, route, position }
 */
export function getBillingTabs() {
    const orgStore = useOrgStore();

    if (!gatesAnswerable()) return [];

    return modules
        .filter(m => typeof m.billingTabs === 'function')
        .filter(m => !m.slug || orgStore.isModuleEnabled(m.slug))
        .flatMap(m => m.billingTabs())
        .filter(tab => !tab.module || orgStore.isModuleEnabled(tab.module))
        .filter(tab => !tab.admin || orgStore.isAdmin)
        .filter(tab => !tab.ability || orgStore.can(tab.ability))
        .sort((a, b) => (a.position ?? 99) - (b.position ?? 99));
}

/**
 * Get the tabs rendered in the settings-crm vertical tab menu.
 * The screen belongs to Contacts, which every plan includes; each module that
 * has something to configure there (Opportunities, Contracts…) contributes its
 * own tab through `crmTabs`, with its own permission. Filtered and sorted like
 * the General tabs.
 * @returns {Array} Tab definitions { title, icon, route, position }
 */
export function getCrmTabs() {
    const orgStore = useOrgStore();

    if (!gatesAnswerable()) return [];

    return modules
        .filter(m => typeof m.crmTabs === 'function')
        .filter(m => !m.slug || orgStore.isModuleEnabled(m.slug))
        .flatMap(m => m.crmTabs())
        .filter(tab => !tab.module || orgStore.isModuleEnabled(tab.module))
        .filter(tab => !tab.admin || orgStore.isAdmin)
        .filter(tab => !tab.ability || orgStore.can(tab.ability))
        .sort((a, b) => (a.position ?? 99) - (b.position ?? 99));
}

/**
 * Get all contact types from modules, filtered by enabled modules and abilities.
 * Each module with a `contactTypes` array contributes type definitions
 * (e.g., Accounting provides 'customer' and 'supplier', Opportunities provides 'prospect').
 * @returns {Array} Contact type definitions available for the current org
 */
export function getContactTypes() {
    const orgStore = useOrgStore();

    return modules
        .filter(m => Array.isArray(m.contactTypes))
        .filter(m => !m.slug || orgStore.isModuleEnabled(m.slug))
        .filter(m => !m.ability || orgStore.canAny(m.ability))
        .flatMap(m => m.contactTypes);
}

/**
 * Which screens each ability opens, read from the menus themselves.
 *
 * Someone building a plan ticks « Comptabilité » and has no way of knowing what
 * that buys — the name of an ability is not a list of screens, and a list
 * written by hand beside it would be wrong within a release. The menu already
 * states it: every entry names the ability it needs, so collecting them the
 * other way round answers the question and keeps answering it.
 *
 * Unfiltered on purpose: this describes what a module *offers*, not what the
 * organization in front of us holds. The plan screen is platform-side, where
 * there is no organization to ask.
 *
 * @returns {Object<string, string[]>} ability → screen names
 */
export function screensByAbility() {
    const screens = {};

    for (const module of modules) {
        if (typeof module.nav !== 'function') continue;

        const nav = module.nav();

        for (const section of Array.isArray(nav) ? nav : [nav]) {
            for (const item of section.items ?? []) {
                // An entry with no ability of its own rides on its module's.
                const ability = item.ability || module.ability || module.slug;

                if (!ability || !item.title) continue;

                (screens[ability] ||= []).push(item.title);
            }
        }
    }

    return screens;
}

/**
 * Get navigation items grouped by section, filtered by enabled modules and abilities.
 * Nav is a function in each module (lazy) so that i18n.t() is not
 * called at import time — routes.js imports modules before i18n is ready.
 * @param {string} section - 'crm', 'projects', 'support', 'finance', or 'operations'
 * @returns {Array} Navigation items for that section
 */
export function getNavItems(section) {
    const orgStore = useOrgStore();

    if (!gatesAnswerable()) return [];

    return (
        modules
            .filter(m => typeof m.nav === 'function')
            .filter(m => !m.slug || orgStore.isModuleEnabled(m.slug))
            .filter(m => !m.ability || orgStore.canAny(m.ability))
            .flatMap(m => {
                const nav = m.nav();
                return Array.isArray(nav) ? nav : [nav];
            })
            .filter(nav => nav.section === section)
            .flatMap(nav => nav.items)
            // An item may also name a *different* module from the one contributing
            // it: Accounting offers the Tax Reports entry, but the screen behind it
            // belongs to Reports, and an organization can hold one without the other.
            //
            // An item may name an ability of its own, finer than its module's. A
            // module is not always the unit that gets sold — an organization can
            // hold Accounting for quoting alone — and without this the menu would
            // offer invoicing that the server then refuses, which reads as a broken
            // application rather than as something not bought. A section left with
            // no item disappears (see config/nav.js).
            .filter(item => !item.module || orgStore.isModuleEnabled(item.module))
            .filter(item => !item.ability || orgStore.canAny(item.ability))
            .filter(item => typeof item.visible !== 'function' || item.visible(orgStore))
            .sort((a, b) => {
                const pa = a.position ?? Infinity;
                const pb = b.position ?? Infinity;
                if (pa !== pb) return pa - pb;
                return (a.title || '').localeCompare(b.title || '');
            })
    );
}
